/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {TextField, TextFieldProps} from '@material-ui/core';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getJob, loadDeviceFiles} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Device,
  DeviceFile,
  Devices,
  DownloadJob,
  DownloadJobTask,
  DownloadJobTasks,
  DownloadJobTasksElem,
  DownloadJobType,
  Entity,
  EntityStatus,
  PlatformFileType
} from '@ion/core/src/types/entities';
import {
  HierLevel,
  ServiceError,
  SmartSearch
} from '@ion/core/src/types/services';
import {getUniqueArray} from '@ion/core/src/utils/ArrayUtil';
import {timeFormatter} from '@ion/core/src/utils/DateUtil';
import {Mapped} from '@ion/core/src/utils/ObjectUtil';
import {
  isDefaultQueryString as isCoreDefaultQueryString
} from '@ion/core/src/utils/Validators';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {getAppId} from '@ion/dls/src/editors/DeviceFileEditorUtils';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {FilterChips} from '@ion/dls/src/types/editors';
import {DEFAULT_SMART_SEARCH_QUERY} from '../../pages/devices/state';
import {SearchFields} from '../../pages/devices/types';

/**
 * Define the default value for device type / file type.
 */
export const CONFIG = 'Config';

// Defines the supported device types for device platforms
export const DEFAULT_DEVICE_TYPES = ['JPOD2', 'OBD II Type II', 'VBU3'];

// Defines the supported queryString for smartSearch
export const BATCH_SIZE = 1000;

/**
 * Renders options in chooser components (ex. JobTaskDeviceTypeChooser),
 * limiting those whose length exceeds 40 characters with an ellipsis.
 */
export const onRenderOption = (
  {name}: {name: string}
): JSX.Element => {
  const maxLength = 40;
  return (
    <span title={name}>
      {name?.length > maxLength ? name.substring(0, maxLength) + '...' : name}
    </span>
  );
};

/**
 * Renders options in the JobTaskAppIdChooser component, showing the app IDs
 * available for the selected devices (and potentially device type).
 */
export const onRenderAppIdOption = (appId: string): JSX.Element => (
  <span>{`${appId}`}</span>
);

/**
 * Renders options in the JobFileNameChooser component, showing the
 * file name and the account to which the file belongs.
 */
export const onRenderFileNameOption = ({
  name,
  description,
  account,
  validationMetaData,
  _self
}: DeviceFile): JSX.Element => {
  const maxLength        = 40;
  const tooltipMaxLength = 280;
  const {
    version,
    fromVersion,
    reportedName
  } = validationMetaData?.fields || {};
  const {deviceTypeName}    = validationMetaData || {};
  const DEFAULT_DEVICE_TYPE = 'Android MDT';
  const item                = deviceTypeName === DEFAULT_DEVICE_TYPE ? reportedName : name;
  const appId               = getAppId({name: item, validationMetaData: validationMetaData, _self: _self}) || '';
  const value               = item?.length > maxLength ? item.substring(0, maxLength) : (item || '');
  const accountTitle        = account?.title || '';
  const fileNameUI          = getFileLabel(fromVersion, version, appId);
  const tooltipTitle        = fileNameUI ? `${fileNameUI}-${item}, ${description || ''} (${accountTitle})` : `${item}, ${description || ''} (${accountTitle})`;
  const tooltip             = tooltipTitle?.length > tooltipMaxLength ? tooltipTitle.substring(0, tooltipMaxLength).concat('...') : (tooltipTitle || '');
  return (
    <Tooltip title={<span>{tooltip}</span>}
      placement={'top'}
      arrow>
      <span style={{textOverflow: 'ellipsis'}}>
        {fileNameUI ? `${fileNameUI}-${value} (${accountTitle})` : `${value} (${accountTitle})`}
      </span>
    </Tooltip>
  );
};

/**
 * Renders the input control in chooser components, specifying the placeholder
 * string identifier.
 */
export const onRenderInput = (placeholder: string) => (params: TextFieldProps): JSX.Element =>
  <TextField {...params} placeholder={placeholder} />;

/**
 * Indicates whether the specified job search criteria includes a smart search
 * indicator to include sub-account device information.
 */
export const includeSubAccountDevices = ({criteria}: DownloadJob): boolean => (
  !!criteria?.some(subCriteria =>
    String(subCriteria.smartSearch?.hierLevel).toUpperCase() === HierLevel.ALL.toUpperCase())
);

/**
 * Indicates whether the specified smart search query string contains specific
 * device list information (device ID(s) or ESN(s)).
 */
export const includesSpecificDevices = (queryString: string): boolean => (
  /[\s\(]*(id)[\s]*[:][\s\(]*[\d]/g.test(queryString) ||
  /[\s\(]*(esn)[\s]*[:][\s\(]*[\d]/g.test(queryString)
);

/**
 * Indicates whether the specified smart search query string is the default
 * (i.e., 'id: (*)' or '(id: *)' or '((id: *))').
 */
const isDefaultQueryString = (queryString: string): boolean => isCoreDefaultQueryString(queryString);

/**
 * Indicates whether the specified smart search filter (i.e., element of a query string)
 * is an "Any Field" filter (no field specified, only a value).
 */
const isFilterAny = (filter: string): boolean => !filter.includes(':');

/**
 * Parses a smart search query string into a sequence of individual filters, stripping
 * out parentheses and creating comma-separated lists of filter values.
 */
const parseQueryStringFilters = (queryString: string): string[] => (
  queryString
    .replace(/[\(\)]/g, '')
    .split('AND')
    .map(filter => filter.replace(/[\s]*(OR)[\s]*/g, ', ').trim())
);

/**
 * Resolves a smart search filter value.
 */
const resolveFilterValue = (
  {key, val, devices, groups}: {key: SearchFields, val: any, devices: Devices, groups: Mapped}
): string => {
  let value = '';
  switch (key) {
    case SearchFields.TYPE:
      value = devices[0]?.type?.title || '';
      break;
    case SearchFields.ID_REPORT_TIMESTAMP:
    case SearchFields.LAST_REPORT_DATE:
      value = val.replace(/\\:/g, ':');
      break;
    case SearchFields.GROUPS:
      value = groups[val]?.name;
      break;
    default:
      value = val as string;
      break;
  };
  return value?.trim();
};

/**
 * Creates a list of smart search filters from the specified query string, using device and group
 * information to resolve filter values (identifiers => names) as needed.
 */
export const getSelectedFilters = (
  queryString: string,
  devices: Devices,
  groups: Mapped
): FilterChips => {
  const labels = translate('devicefilters:filters', {returnObjects: true});
  const filters: FilterChips = [];
  if (queryString && !isDefaultQueryString(queryString)) {
    const queryStringFilters: string[] = parseQueryStringFilters(queryString);
    queryStringFilters.forEach(qsf => {
      if (!isFilterAny(qsf)) {
        const ext   = 'extensions.';
        const start = qsf.startsWith(ext) ? ext.length : 0;
        const pos   = qsf.indexOf(':');
        const key   = qsf.slice(start, pos).trim() as SearchFields;
        const val   = qsf.slice(pos + 1).trim();
        if (labels[key]) {
          const value = resolveFilterValue({key, val, devices, groups});
          filters.push({
            label     : `${labels[key]}: ${value}`,
            attribute : key,
            value
          });
        }
      } else {
        filters.push({
          label     : `${labels.any}: ${qsf}`,
          attribute : 'any',
          value     : qsf.trim()
        });
      }
    });
  }
  return filters;
};

/**
 * Creates a smart search query string from the specified job criteria (which may
 * be using either a smart search or a member search).
 */
export const getJobQueryString = ({criteria}: DownloadJob): string => {
  const result = criteria?.map(subCriteria => {
    const {smartSearch, memberSearch} = subCriteria;
    if (smartSearch) {
      return smartSearch.queryString;
    } else if (memberSearch) {
      const {esn} = memberSearch.searchTerms;
      if (esn) {
        return `esn: (${esn})`;
      }
    }
    return DEFAULT_SMART_SEARCH_QUERY;
  });
  return result?.length ? `(${result.join(') AND (')})` : DEFAULT_SMART_SEARCH_QUERY;
};

/**
 * Indicates whether the specified device file type utilizes app IDs.
 */
export const fileTypeUsesAppIds = (fileType: PlatformFileType): boolean => (
  !!fileType?.inputFields?.some(({name}) => name === 'appId')
);

/**
 * Resolves File properties and returns string to show as dropdown option and Value.
 */
export const getFileLabel = (version: string, fileVersion: string, appId: string): string => {
  let label1 = '';
  if (fileVersion) {
    if (appId) {
      label1 = `${fileVersion} - ${appId}`;
    } else {
      label1 = `${fileVersion}`;
    }
  } else {
    label1 = appId ? `${appId}` : '';
  }
  return version ? `${translate('common:labels:from')}: ${version} - ${label1}` : label1;
};

/**
 * Returns list of ids from the provided devices.
 */
export const getIds = (devices) => (!!devices?.length && devices?.map(({id}) => id)) || [];

/**
 * Uses device file properties to determine the string to show for a dropdown option/value.
 */
export const getFileNameDisplay = (deviceFile: DeviceFile) => {
  const {fields}      = deviceFile?.validationMetaData;
  const fileVersion   = deviceFile?.version;
  const fromVersion   = fields?.fromVersion;
  const fileVersionUI = fields?.version;
  const appId         = getAppId(deviceFile) || '';
  const fileNameUI    = getFileLabel(fromVersion, fileVersionUI, appId);
  const fileLabel     = `${deviceFile?.name}${(fileVersion ? `(Version:${fileVersion})` : '')}`;
  return {
    fileNameUI,
    fileLabel
  };
};

/**
 * Extracts a smart search query string from the specified job criteria (which may
 * be using either a smart search or a member search).
 */
export const getSearchQueryString = (search: SmartSearch): string => (
  search?.smartSearch?.queryString || DEFAULT_SMART_SEARCH_QUERY
);

/**
 * Parses the given service error to extract the appropriate error message.
 */
export const getJobErrorText = ({errorText, message}: ServiceError, isRerun = false): string => {
  const defaultErrorMessage = message || translate('jobcreate:errors.unknown');
  const errorMessage        = errorText?.error?.description || defaultErrorMessage;
  const errorTextMessage    = errorText || defaultErrorMessage;
  return isRerun ? errorMessage : errorTextMessage;
};

/**
 * Returns the app IDs (as an array) associated with the specified device file.
 */
export const getFileAppIds = (file: DeviceFile): string[] => (
  getAppId(file)?.split(',')?.map(id => id.trim()) || []
);

// "Clones" the tasks within the specified job data to create a series of
// new tasks to be submitted as part of a new job (either cloned or rerun)
export const cloneJobTasks = async(job: DownloadJob): Promise<DownloadJobTasks> => {
  const {tasks} = job || {};
  if (!isNil(tasks)) {
    try {
      const clonedTasks = await Promise.all(tasks?.map?.(async({task}, index) => {
        const {name, description, taskType, metaData} = task || {};
        if (taskType === 'File') {
          let clonedTask = {
            ...initializeNewJobTask(index),
            taskType,
            name,
            description,
            metaData
          };
          const id      = entityId(metaData?.file);
          const {items} = await loadDeviceFiles({
            includeAccountFilter : false,
            filterOn             : {id}
          }) || {};
          if (items?.length) {
            const file = items?.[0] as DeviceFile;
            const {
              name               : fileName,
              validationMetaData : {
                deviceTypeId,
                deviceTypeName,
                fileTypeId,
                fileTypeName,
                platformId
              }
            } = file || {};
            const fileAppIds = getFileAppIds(file);
            const {
              fileNameUI,
              fileLabel
            } = getFileNameDisplay(file);
            clonedTask = {
              ...clonedTask,
              deviceType : deviceTypeName,
              fileType   : fileTypeName,
              usesAppIds : !!fileAppIds.length,
              appId      : fileAppIds.length === 1 ? fileAppIds[0] : '',
              deviceTypeId,
              fileTypeId,
              platformId,
              fileAppIds,
              fileName,
              fileNameUI,
              fileLabel
            };
          } else {
            clonedTask = {
              ...clonedTask,
              metaData : {}
            };
          }
          return {
            task : clonedTask
          };
        } else {
          return Promise.resolve({
            task : {
              ...task,
              progressStatus : DownloadJobType.Pending,
              index
            }
          });
        }
      }));
      return clonedTasks;
    } catch (err) {
      error(err);
    }
  }
};

// Creates a new job name for the specified job to be cloned or rerun.
export const createClonedJobName = (job: DownloadJob, isRerun: boolean = false): string => {
  const op = !isRerun ? '_clone_' : '_rerun_';
  const now = timeFormatter(new Date(), 'YYYYMMDD_HHmmss');
  let {name} = job;
  const index = name?.indexOf(op);
  if (index !== -1) {
    name = name.slice(0, index);
  }
  return `${name}${op}${now}`;
};

// "Clones" the specified job data to create a new job.
export const cloneJobInfo = async(job: DownloadJob, isRerun: boolean = false): Promise<DownloadJob> => {
  const jobOrig = await getJob(job?.id);
  const tasks   = await cloneJobTasks(job);
  const {description, account, criteria} = jobOrig;
  return {
    name           : createClonedJobName(jobOrig, isRerun),
    description    : description || null,
    progressStatus : DownloadJobType.Pending,
    account,
    criteria,
    tasks
  };
};

// Initializes and returns a new job task structure.
export const initializeNewJobTask = (index: number): DownloadJobTask => ({
  index          : index,
  taskType       : 'File',
  name           : 'task_' + index,
  identifier     : '',
  children       : [],
  description    : '',
  status         : EntityStatus.Enabled,
  progressStatus : DownloadJobType.Pending,
  metaData       : {},
  deviceType     : '',
  fileType       : '',
  deviceTypeId   : '',
  fileTypeId     : '',
  platformId     : '',
  usesAppIds     : false,
  appId          : '',
  deviceAppIds   : [],
  fileAppIds     : [],
  fileName       : '',
  fileNameUI     : '',
  fileLabel      : ''
});

/**
 * Returns whether any task files do not exist for the specified job. Used in
 * conjunction with initializeNewJobTask and cloneJobTasks helper functions
 * above (which set the task file meta data to an empty object when no task
 * file information is available).
 */
export const jobTaskFilesNotFound = (job: DownloadJob): boolean => (
  !job || !job.tasks || job.tasks.some(({task}) => isEmpty(task?.metaData))
);

/**
 * Normalizes job task information before sending it as part of the job
 * to be created.
 */
export const normalizeJobTasks = (job: DownloadJob) => {
  job?.tasks?.forEach((item, index) => {
    const {task} = item || {};
    task.name           = 'task_' + index;
    task.status         = EntityStatus.Enabled;
    task.progressStatus = DownloadJobType.Pending;
    delete task.index;
    delete task.identifier;
    delete task.children;
    delete task.deviceTypeId;
    delete task.fileTypeId;
    delete task.platformId;
    delete task.deviceAppIds;
    delete task.fileAppIds;
    delete item.id;
    delete item.trackLine;
    delete item.ancestorIds;
  });
  return job;
};

/**
 * Normalizes (sorts and removes any duplicates from) the specified app ID array.
 */
export const normalizeAppIds = (appIds: string[]): string[] => (
  Array.from(new Set(appIds)).sort((a, b) => Number(a) - Number(b))
);

/**
 * Getting platform Ids from the selected devices.
 */
export const getPlatformIds = (devices: Device[]) => {
  const platforms = [];
  devices?.forEach(({extensions}) => {
    if (extensions?.platforms?.length) {
      platforms.push(extensions?.platforms);
    }
  });
  if (platforms?.length) {
    return getUniqueArray(
      platforms?.map(platform => platform?.map(platformObj => platformObj?.platformId)).flat()
    );
  }
  return [];
};

/**
 * Creates a batches of smart search criteria object if selected devices exceeds the limit (1000).
 */
export const getMaxSearchWithIds = (devices, accountId, includeSubAccounts) => {
  const hierLevel = includeSubAccounts ? {hierLevel: HierLevel.ALL} : {};
  const batches   = [];
  devices?.forEach(({id}, index) => {
    if (!isNil(id)) {
      const currentBatchIndex = Math.floor(index / BATCH_SIZE);
      if (!batches[currentBatchIndex]) {
        batches[currentBatchIndex] = {
          smartSearch : {
            queryString : 'id: (',
            type        : Entity.device,
            ...hierLevel,
            accountId
          }
        };
      }
      const {smartSearch} = batches[currentBatchIndex];
      smartSearch.queryString += `${id} OR `;
      if ((index + 1) % BATCH_SIZE === 0 || index === devices.length - 1) {
        const queryString       = smartSearch.queryString;
        smartSearch.queryString = `${queryString.slice(0, -4)})`;
      }
    }
  });
  return batches;
};

/**
 * Getting the dependent task dropdown options list ....
 */
export const getDependentTaskOptions = (tasks: DownloadJobTasks) => (
  Array.isArray(tasks)
    ? tasks.map((task, index) => ({
      label : `${index + 1}`,
      value : task
    }))
    : []
);

/**
 * This function is used to update the dependencies of tasks in currentTasks based on
 * their order in prevTasks. It helps reorder the dependencies if any task dependencies
 * change when tasks are added or removed
 * @param prevTasks array of task objects before a task was deleted.
 * @param currentTasks array of task objects after a task has been deleted.
 * @returns Return the updated task
 */
export const reorderTaskDependencies = (
  prevTasks: DownloadJobTasks,
  currentTasks: DownloadJobTasks
): DownloadJobTasksElem[] => {
  const cloneCurrentTasks        = cloneDeep(currentTasks);
  const taskIdentifierToIndexMap = new Map(
    cloneCurrentTasks?.map((task, index) => [task.id, index])
  );
  return cloneCurrentTasks?.map((currentTask) => {
    const currentDependencyIndex = currentTask.task.metaData?.dependency?.index;
    const prevDependencyTask = prevTasks?.find(
      (_, index) => index === currentDependencyIndex
    );
    const prevDependencyIdentifier = prevDependencyTask
      ? prevDependencyTask.id
      : null;
    if (prevDependencyIdentifier && taskIdentifierToIndexMap.has(prevDependencyIdentifier)) {
      const currentDependencyTaskIndex = taskIdentifierToIndexMap.get(
        prevDependencyIdentifier
      );
      if (currentDependencyTaskIndex >= 0) {
        currentTask.task.metaData.dependency.index = currentDependencyTaskIndex;
      }
    } else {
      delete currentTask.task.metaData?.dependency;
    }
    return currentTask;
  });
};

/**
 * This function takes the index of a task and returns the corresponding color object.
 */
export const dependentTaskColors = (index: number) => {
  const colors = [
    {
      main  : dlsColor('caBlue'),
      light : '#D4F1F9'
    },
    {
      main  : dlsAlt('maps', 'colors', 'pink'),
      light : '#FBE1E8'
    },
    {
      main  : dlsColor('caGreen'),
      light : '#D9F0B4'
    },
    {
      main  : dlsAlt('maps', 'colors', 'orange'),
      light : '#FCE6B4'
    },
    {
      main  : dlsColor('caYellow'),
      light : '#FEF5A5'
    },
    {
      main  : dlsColor('caRed'),
      light : '#F9D0B3'
    },
    {
      main  : dlsColor('darkTeal'),
      light : '#A6D0D9'
    },
    {
      main  : dlsAlt('maps', 'colors', 'midnight'),
      light : '#E3C1D6'
    }
  ];
  return colors[index] || {main: dlsColor('grey1'), light: dlsColor('white')};
};

/**
 * This function is used to find the index of the last child task within a given list of tasks array
 * @param tasks An array of task objects, Each task may or may not have a children array, which contains
 * the IDs of the task's child tasks.
 * @param item A task whose children need to be checked.
 * @returns The index of the last child in the tasks array. If no child tasks are found, it returns -1.
 */
export const getLastChildIndex = (tasks: DownloadJobTasksElem[], item: DownloadJobTasksElem) => {
  const {children}  = item?.task || {};
  let endChildIndex = tasks?.findIndex(item => item.id === children?.[0]);
  children?.forEach((child) => {
    const currentIndex = tasks?.findIndex(item => item.id === child);
    if (currentIndex > endChildIndex) {
      endChildIndex = currentIndex;
    }
  });
  return endChildIndex;
};

/** The `getAncestorIds` function is a recursive utility that generates a list of
 * ancestor task IDs for a given task, including any dependencies it might have.
 * It traverses through the task dependency hierarchy, starting from the provided
 * `parentIndex`, and collects the IDs of all ancestor tasks in an array.
 * @param   allTasks An array of all tasks.
 * @param   parentIndex The index of the task.
 * @param   ancestorIds An accumulator array that keeps track of all ancestor
 *          task IDs.
 * @returns Returns an array of task IDs, where each ID represents an ancestor
 *          of the given task, starting from the immediate parent and including
 *          all parent tasks up the chain.
 */
export const getAncestorIds = (
  allTasks: DownloadJobTasks, parentIndex: number, ancestorIds: string[] = []): string[] => {
  if (parentIndex >= 0) {
    const parentTask   = allTasks[parentIndex];
    ancestorIds.push(parentTask.id);
    const dependencyIndex = parentTask?.task?.metaData?.dependency?.index;
    if (dependencyIndex >= 0) {
      getAncestorIds(allTasks, dependencyIndex, ancestorIds);
    }
  }
  return ancestorIds;
};
